import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ChevronLeft,
  ChevronRight,
  Sun,
  Pause,
  Edit2,
  X,
  Save,
  Factory,
  Printer,
  ImageDown,
  Download,
  Share2,
  Settings,
  Calendar
} from 'lucide-react';
import html2canvas from 'html2canvas';
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  addMonths,
  subMonths,
  getDay,
  startOfWeek,
  parseISO
} from 'date-fns';
import { getRotationWeekNumber, getDaySchedule, getCustomDayRecipe } from '../utils/rotationUtils';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const esc = (s) => String(s ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

export const LiveCalendar = () => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [regularRotation, setRegularRotation] = useState(null);
  const [specialRotation, setSpecialRotation] = useState(null);
  const [customRotations, setCustomRotations] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rotationStartDate, setRotationStartDate] = useState(null);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [tempStartDate, setTempStartDate] = useState('');
  const { isAdmin } = useAuth();

  const [selectedDate, setSelectedDate] = useState(null);
  const [showOverrideDialog, setShowOverrideDialog] = useState(false);
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [calendarImage, setCalendarImage] = useState(null);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [imageLayout, setImageLayout] = useState('portrait');
  const [overrideData, setOverrideData] = useState({
    override_type: 'regular',
    sabzi: '',
    daal: '',
    salad: '',
    extra: '',
    main: '',
    roti: '',
    raita: '',
    side: '',
    reason: ''
  });

  const navigate = useNavigate();
  const printRef = useRef(null);

  const buildPrintStyles = () => `
          .cal-print-root {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            font-size: 11px;
            color: #1c1917;
            background: #ffffff;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .cal-print-root * {
            box-sizing: border-box;
            margin: 0;
            padding: 0;
          }
          .print-header {
            text-align: center;
            margin-bottom: 20px;
            padding-bottom: 15px;
            border-bottom: 3px solid #124242;
          }
          .print-header h1 {
            font-size: 34px;
            color: #124242;
            margin-bottom: 6px;
          }
          .print-header p {
            color: #78716c;
            font-size: 16px;
          }
          .calendar-grid {
            width: 100%;
            border-collapse: collapse;
          }
          .calendar-grid th {
            background: #124242;
            color: white;
            padding: 12px 5px;
            text-align: center;
            font-weight: 600;
            font-size: 15px;
          }
          .calendar-grid td {
            border: 1px solid #d6d3d1;
            padding: 9px 8px;
            vertical-align: top;
            width: 14.28%;
          }
          .calendar-grid td.special {
            background: #fef3c7;
          }
          .calendar-grid td.saturday {
            background: #f5f5f4;
          }
          .calendar-grid td.empty {
            background: #fafaf9;
          }
          .day-number {
            font-weight: 700;
            font-size: 18px;
            color: #124242;
            margin-bottom: 6px;
          }
          .day-number.special {
            color: #d97706;
          }
          .menu-item {
            font-size: 16px;
            font-weight: 600;
            line-height: 1.35;
            padding: 3px 0;
            white-space: normal;
            word-break: break-word;
          }
          .menu-item.sabzi { color: #15803d; }
          .menu-item.daal { color: #c2410c; }
          .menu-item.salad { color: #0e7490; }
          .menu-item.custom { color: #7c3aed; }
          .menu-item.special-main { color: #d97706; font-weight: 700; }
          .menu-item.special-other { color: #57534e; }
          .day-off {
            text-align: center;
            color: #a8a29e;
            font-style: italic;
            font-size: 14px;
            padding-top: 25px;
          }
          .override-badge {
            display: inline-block;
            width: 8px;
            height: 8px;
            background: #faa023;
            border-radius: 50%;
            margin-left: 5px;
          }
          .legend {
            margin-top: 20px;
            padding-top: 15px;
            border-top: 1px solid #d6d3d1;
            display: flex;
            gap: 25px;
            justify-content: center;
            flex-wrap: wrap;
          }
          .legend-item {
            display: flex;
            align-items: center;
            gap: 6px;
            font-size: 13px;
          }
          .legend-dot {
            width: 12px;
            height: 12px;
            border-radius: 50%;
          }
          .legend-dot.sabzi { background: #15803d; }
          .legend-dot.daal { background: #c2410c; }
          .legend-dot.salad { background: #0e7490; }
          .legend-dot.custom { background: #7c3aed; }
          .legend-dot.special { background: #d97706; }
          .legend-dot.override { background: #faa023; }
          .footer {
            margin-top: 15px;
            text-align: center;
            color: #a8a29e;
            font-size: 12px;
          }
  `;

  const buildPrintBody = () => `
      <div class="cal-print-root">
        <div class="print-header">
          <h1>${format(currentMonth, 'MMMM yyyy')}</h1>
          <p>VPrep - Kitchen Calendar</p>
        </div>
        
        <table class="calendar-grid">
          <thead>
            <tr>
              <th>Sunday</th>
              <th>Monday</th>
              <th>Tuesday</th>
              <th>Wednesday</th>
              <th>Thursday</th>
              <th>Friday</th>
              <th>Saturday</th>
            </tr>
          </thead>
          <tbody>
            ${generatePrintCalendarRows()}
          </tbody>
        </table>
        
        <div class="legend">
          <div class="legend-item"><div class="legend-dot sabzi"></div> Sabzi</div>
          <div class="legend-item"><div class="legend-dot daal"></div> Daal</div>
          <div class="legend-item"><div class="legend-dot salad"></div> Salad</div>
          <div class="legend-item"><div class="legend-dot custom"></div> Dessert</div>
          <div class="legend-item"><div class="legend-dot special"></div> Special Menu</div>
          <div class="legend-item"><div class="legend-dot override"></div> Has Override</div>
        </div>
        
        <div class="footer">
          Printed on ${format(new Date(), 'MMMM d, yyyy')} at ${format(new Date(), 'h:mm a')}
        </div>
      </div>
  `;

  // Compact sizing shared by the print page and the landscape image (so they match)
  const printCompactStyles = `
      .cal-print-root .print-header { margin-bottom: 10px; padding-bottom: 8px; }
      .cal-print-root .print-header h1 { font-size: 24px; }
      .cal-print-root .print-header p { font-size: 12px; }
      .cal-print-root .calendar-grid th { font-size: 12px; padding: 6px 4px; }
      .cal-print-root .calendar-grid td { padding: 4px 5px; }
      .cal-print-root .day-number { font-size: 14px; margin-bottom: 2px; }
      .cal-print-root .menu-item { font-size: 12.5px; line-height: 1.25; padding: 1px 0; }
      .cal-print-root .day-off { font-size: 10px; padding-top: 10px; }
      .cal-print-root .legend { margin-top: 8px; padding-top: 6px; gap: 16px; }
      .cal-print-root .legend-item { font-size: 10px; }
      .cal-print-root .legend-dot { width: 9px; height: 9px; }
      .cal-print-root .footer { margin-top: 6px; font-size: 9px; }
  `;

  const getImageFileName = () => {
    if (imageLayout === 'chart') return 'virsa-rotation-chart.png';
    if (imageLayout === 'chartv') return 'virsa-rotation-chart-days-column.png';
    return `virsa-calendar-${format(currentMonth, 'MMMM-yyyy')}${imageLayout === 'portrait' ? '-phone' : ''}.png`;
  };

  const handleShareImage = async () => {
    if (!calendarImage) return;
    try {
      const res = await fetch(calendarImage);
      const blob = await res.blob();
      const file = new File([blob], getImageFileName(), { type: 'image/png' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: 'VPrep — Menu' });
      } else {
        toast.info('Sharing is not supported on this device/browser — use Download instead');
      }
    } catch (error) {
      if (error?.name !== 'AbortError') {
        toast.error('Could not share the image');
      }
    }
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    printWindow.document.title = `Calendar - ${format(currentMonth, 'MMMM yyyy')}`;
    printWindow.document.head.innerHTML += `<style>
      @page { size: A4 landscape; margin: 6mm; }
      body { margin: 0; }
      .cal-print-root { width: 283mm; margin: 0 auto; }
      ${buildPrintStyles()}
      ${printCompactStyles}
    </style>`;
    printWindow.document.body.innerHTML = buildPrintBody();
    setTimeout(() => {
      // Auto-fit to a single A4 landscape page: shrink content if taller than the printable area
      const root = printWindow.document.querySelector('.cal-print-root');
      if (root) {
        const printableHeightPx = (196 * 96) / 25.4; // 210mm - 2x6mm margins, slight safety
        const contentHeight = root.scrollHeight;
        if (contentHeight > printableHeightPx) {
          root.style.zoom = (printableHeightPx / contentHeight).toFixed(3);
        }
      }
      printWindow.print();
    }, 300);
  };

  const buildChartStyles = () => `
      .rc-root {
        width: 540px;
        height: 699px;
        font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        background: #ffffff;
        color: #1c1917;
        padding: 14px 16px 8px;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
      }
      .rc-root.rc-v {
        height: auto;
        min-height: 699px;
        padding-bottom: 14px;
      }
      .rc-root * { box-sizing: border-box; margin: 0; padding: 0; }
      .rc-header { text-align: center; padding-bottom: 7px; border-bottom: 3px solid #124242; margin-bottom: 9px; }
      .rc-header h1 { font-size: 22px; color: #124242; }
      .rc-header p { font-size: 11px; color: #78716c; margin-top: 2px; }
      .rc-week { display: flex; flex-direction: column; margin-bottom: 6px; }
      .rc-week-title {
        font-size: 14px;
        font-weight: 700;
        color: #d97706;
        margin-bottom: 4px;
        text-align: center;
        text-transform: uppercase;
        letter-spacing: 1px;
      }
      .rc-table { width: 100%; border-collapse: collapse; table-layout: fixed; }
      .rc-table td {
        border: 1px solid #d6d3d1;
        padding: 8px 4px;
        text-align: center;
        font-size: 12.5px;
        font-weight: 600;
        line-height: 1.25;
        word-break: break-word;
        vertical-align: middle;
      }
      .rc-table .rc-label { width: 54px; background: #124242; color: #ffffff; font-size: 12px; font-weight: 700; }
      .rc-days td { background: #f5f5f4; color: #124242; font-size: 12px; }
      .rc-daal td { color: #c2410c; }
      .rc-sabzi td { color: #15803d; }
      .rc-salad td { color: #0e7490; }
      .rc-daal .rc-label, .rc-sabzi .rc-label, .rc-salad .rc-label { color: #ffffff; }
      .rc-footer { text-align: center; font-size: 10px; color: #a8a29e; }
      .rc-v .rc-table td { font-size: 11.5px; padding: 6px 4px; }
      .rc-vlabel { background: #124242; color: #ffffff !important; font-size: 12px; font-weight: 700; }
      .rc-day-col { width: 88px; }
      .rc-day { background: #f5f5f4; color: #124242; font-size: 12px; }
      .rc-cell-daal { color: #c2410c; }
      .rc-cell-sabzi { color: #15803d; }
      .rc-cell-salad { color: #0e7490; }
  `;

  const buildChartBody = () => {
    const weekLabels = ['First Week', 'Second Week', 'Third Week', 'Fourth Week', 'Fifth Week', 'Sixth Week', 'Seventh Week', 'Eighth Week'];
    const totalWeeks = regularRotation?.rotation_length || 3;
    const days = regularRotation?.weekdays || ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
    const schedule = regularRotation?.schedule || {};
    const rowDefs = [['daal', 'Daal'], ['sabzi', 'Sabzi'], ['salad', 'Salad']];

    const weeks = Array.from({ length: totalWeeks }, (_, i) => {
      const wk = schedule[`Week${i + 1}`] || {};
      const categoryRows = rowDefs.map(([field, label]) => `
          <tr class="rc-${field}">
            <td class="rc-label">${label}</td>
            ${days.map(d => `<td>${esc(wk[d]?.[field] || '—')}</td>`).join('')}
          </tr>`).join('');
      return `
        <div class="rc-week">
          <div class="rc-week-title">${weekLabels[i] || `Week ${i + 1}`}</div>
          <table class="rc-table">
            <tr class="rc-days"><td class="rc-label">Day</td>${days.map(d => `<td>${d}</td>`).join('')}</tr>
            ${categoryRows}
          </table>
        </div>`;
    }).join('');

    return `
      <div class="rc-root">
        <div class="rc-header">
          <h1>Menu Rotation Chart</h1>
          <p>VPrep — ${totalWeeks}-Week Rotation</p>
        </div>
        ${weeks}
        <div class="rc-footer">Generated on ${format(new Date(), 'MMMM d, yyyy')}</div>
      </div>`;
  };

  const buildChartBodyVertical = () => {
    const weekLabels = ['First Week', 'Second Week', 'Third Week', 'Fourth Week', 'Fifth Week', 'Sixth Week', 'Seventh Week', 'Eighth Week'];
    const totalWeeks = regularRotation?.rotation_length || 3;
    const days = regularRotation?.weekdays || ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
    const schedule = regularRotation?.schedule || {};

    const weeks = Array.from({ length: totalWeeks }, (_, i) => {
      const wk = schedule[`Week${i + 1}`] || {};
      const dayRows = days.map(d => `
          <tr>
            <td class="rc-day">${d}</td>
            <td class="rc-cell-daal">${esc(wk[d]?.daal || '—')}</td>
            <td class="rc-cell-sabzi">${esc(wk[d]?.sabzi || '—')}</td>
            <td class="rc-cell-salad">${esc(wk[d]?.salad || '—')}</td>
          </tr>`).join('');
      return `
        <div class="rc-week">
          <div class="rc-week-title">${weekLabels[i] || `Week ${i + 1}`}</div>
          <table class="rc-table">
            <tr>
              <td class="rc-vlabel rc-day-col">Day</td>
              <td class="rc-vlabel">Daal</td>
              <td class="rc-vlabel">Sabzi</td>
              <td class="rc-vlabel">Salad</td>
            </tr>
            ${dayRows}
          </table>
        </div>`;
    }).join('');

    return `
      <div class="rc-root rc-v">
        <div class="rc-header">
          <h1>Menu Rotation Chart</h1>
          <p>VPrep — ${totalWeeks}-Week Rotation</p>
        </div>
        ${weeks}
        <div class="rc-footer">Generated on ${format(new Date(), 'MMMM d, yyyy')}</div>
      </div>`;
  };

  const handleSaveImage = async (layout = 'portrait') => {
    setImageLayout(layout);
    setGeneratingImage(true);
    try {
      const isPortrait = layout === 'portrait';
      const isChart = layout === 'chart' || layout === 'chartv';
      const isChartV = layout === 'chartv';
      const container = document.createElement('div');
      const width = isChart ? '540px' : isPortrait ? '640px' : '1070px';
      container.style.cssText = `position:fixed;left:-10000px;top:0;width:${width};${isChart && !isChartV ? 'height:699px;' : ''}background:#ffffff;${isPortrait || isChart ? '' : 'padding:16px;'}`;
      container.innerHTML = isChart
        ? `<style>${buildChartStyles()}</style>${layout === 'chartv' ? buildChartBodyVertical() : buildChartBody()}`
        : isPortrait
          ? `<style>${buildMobileStyles()}</style>${buildMobileBody()}`
          : `<style>${buildPrintStyles()}${printCompactStyles}</style>${buildPrintBody()}`;
      document.body.appendChild(container);
      const canvas = await html2canvas(container, {
        scale: layout === 'landscape' || isChart ? 3 : 2,
        backgroundColor: '#ffffff',
        ...(isChart && !isChartV ? { width: 540, height: 699 } : {}),
        ...(isChartV ? { width: 540 } : {})
      });
      document.body.removeChild(container);
      setCalendarImage(canvas.toDataURL('image/png'));
      setShowImageDialog(true);
    } catch (error) {
      toast.error('Failed to generate image');
    } finally {
      setGeneratingImage(false);
    }
  };

  const buildMobileStyles = () => `
      .cal-mobile-root {
        font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        color: #1c1917;
        background: #ffffff;
        width: 100%;
        padding: 12px;
      }
      .cal-mobile-root * { box-sizing: border-box; margin: 0; padding: 0; }
      .cal-mobile-root .m-header {
        text-align: center;
        padding: 10px 10px 12px;
        border-bottom: 3px solid #124242;
        margin-bottom: 10px;
      }
      .cal-mobile-root .m-header h1 { font-size: 26px; color: #124242; }
      .cal-mobile-root .m-header p { font-size: 13px; color: #78716c; margin-top: 2px; }
      .cal-mobile-root .calendar-grid { width: 100%; border-collapse: collapse; }
      .cal-mobile-root .calendar-grid th {
        background: #124242;
        color: #ffffff;
        padding: 7px 2px;
        text-align: center;
        font-weight: 600;
        font-size: 12px;
      }
      .cal-mobile-root .calendar-grid td {
        border: 1px solid #d6d3d1;
        padding: 5px 4px;
        vertical-align: top;
        width: 14.28%;
      }
      .cal-mobile-root .calendar-grid td.special { background: #fef3c7; }
      .cal-mobile-root .calendar-grid td.saturday { background: #f5f5f4; }
      .cal-mobile-root .calendar-grid td.empty { background: #fafaf9; }
      .cal-mobile-root .day-number { font-weight: 700; font-size: 13px; color: #124242; margin-bottom: 3px; }
      .cal-mobile-root .day-number.special { color: #d97706; }
      .cal-mobile-root .menu-item {
        font-size: 12.5px;
        font-weight: 600;
        line-height: 1.3;
        padding: 1.5px 0;
        white-space: normal;
        word-break: break-word;
      }
      .cal-mobile-root .menu-item.sabzi { color: #15803d; }
      .cal-mobile-root .menu-item.daal { color: #c2410c; }
      .cal-mobile-root .menu-item.salad { color: #0e7490; }
      .cal-mobile-root .menu-item.custom { color: #7c3aed; }
      .cal-mobile-root .menu-item.special-main { color: #d97706; font-weight: 700; }
      .cal-mobile-root .menu-item.special-other { color: #57534e; }
      .cal-mobile-root .day-off { text-align: center; color: #a8a29e; font-style: italic; font-size: 11px; padding-top: 8px; }
      .cal-mobile-root .override-badge {
        display: inline-block;
        width: 6px;
        height: 6px;
        background: #faa023;
        border-radius: 50%;
        margin-left: 3px;
      }
      .cal-mobile-root .m-legend {
        margin-top: 10px;
        padding-top: 8px;
        border-top: 1px solid #d6d3d1;
        display: flex;
        gap: 14px;
        justify-content: center;
        flex-wrap: wrap;
      }
      .cal-mobile-root .m-legend-item {
        display: flex;
        align-items: center;
        gap: 5px;
        font-size: 12px;
        font-weight: 600;
      }
      .cal-mobile-root .m-legend-dot { width: 10px; height: 10px; border-radius: 50%; }
      .cal-mobile-root .m-legend-dot.sabzi { background: #15803d; }
      .cal-mobile-root .m-legend-dot.daal { background: #c2410c; }
      .cal-mobile-root .m-legend-dot.salad { background: #0e7490; }
      .cal-mobile-root .m-legend-dot.custom { background: #7c3aed; }
      .cal-mobile-root .m-legend-dot.special { background: #d97706; }
      .cal-mobile-root .m-legend-dot.override { background: #faa023; }
      .cal-mobile-root .m-footer { text-align: center; color: #a8a29e; font-size: 11px; padding: 10px 0 2px; }
  `;

  const buildMobileBody = () => `
      <div class="cal-mobile-root">
        <div class="m-header">
          <h1>${format(currentMonth, 'MMMM yyyy')}</h1>
          <p>VPrep — Kitchen Calendar</p>
        </div>
        <table class="calendar-grid">
          <thead>
            <tr>
              <th>Sun</th><th>Mon</th><th>Tue</th><th>Wed</th><th>Thu</th><th>Fri</th><th>Sat</th>
            </tr>
          </thead>
          <tbody>
            ${generatePrintCalendarRows()}
          </tbody>
        </table>
        <div class="m-legend">
          <div class="m-legend-item"><div class="m-legend-dot sabzi"></div> Sabzi</div>
          <div class="m-legend-item"><div class="m-legend-dot daal"></div> Daal</div>
          <div class="m-legend-item"><div class="m-legend-dot salad"></div> Salad</div>
          <div class="m-legend-item"><div class="m-legend-dot custom"></div> Dessert</div>
          <div class="m-legend-item"><div class="m-legend-dot special"></div> Special Menu</div>
          <div class="m-legend-item"><div class="m-legend-dot override"></div> Has Override</div>
        </div>
        <div class="m-footer">Generated on ${format(new Date(), 'MMMM d, yyyy')} at ${format(new Date(), 'h:mm a')}</div>
      </div>`;

  const generatePrintCalendarRows = () => {
    const weeks = [];
    let currentWeek = [];
    
    // Add padding for start of month
    for (let i = 0; i < startPadding; i++) {
      currentWeek.push(null);
    }
    
    // Add all days
    daysInMonth.forEach(date => {
      currentWeek.push(date);
      if (currentWeek.length === 7) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
    });
    
    // Pad last week if needed
    while (currentWeek.length > 0 && currentWeek.length < 7) {
      currentWeek.push(null);
    }
    if (currentWeek.length > 0) {
      weeks.push(currentWeek);
    }
    
    return weeks.map(week => {
      const cells = week.map(date => {
        if (!date) {
          return '<td class="empty"></td>';
        }
        
        const menu = getDayMenu(date);
        const dayOfWeek = getDay(date);
        const isSunday = dayOfWeek === 0;
        const isSaturday = dayOfWeek === 6;
        const hasOverride = overrides.some(o => o.date === format(date, 'yyyy-MM-dd'));
        
        let classes = [];
        if (menu.type === 'special') classes.push('special');
        if (isSaturday) classes.push('saturday');
        
        let content = `<div class="day-number ${menu.type === 'special' ? 'special' : ''}">${format(date, 'd')}${hasOverride ? '<span class="override-badge"></span>' : ''}</div>`;
        
        if (menu.type === 'off') {
          content += `<div class="day-off">${esc(menu.reason)}</div>`;
        } else if (menu.type === 'special') {
          if (menu.main) content += `<div class="menu-item special-main">${esc(menu.main)}</div>`;
          if (menu.roti) content += `<div class="menu-item special-other">${esc(menu.roti)}</div>`;
          if (menu.raita) content += `<div class="menu-item special-other">${esc(menu.raita)}</div>`;
          if (menu.side) content += `<div class="menu-item special-other">${esc(menu.side)}</div>`;
        } else {
          if (menu.sabzi) content += `<div class="menu-item sabzi">${esc(menu.sabzi)}</div>`;
          if (menu.daal) content += `<div class="menu-item daal">${esc(menu.daal)}</div>`;
          if (menu.salad) content += `<div class="menu-item salad">${esc(menu.salad)}</div>`;
        }
        
        // Custom rotation items (e.g., desserts)
        if (menu.type !== 'off') {
          Object.values(getCustomRotationItems(date)).forEach(item => {
            content += `<div class="menu-item custom">${esc(item.recipe)}</div>`;
          });
        }
        
        return `<td class="${classes.join(' ')}">${content}</td>`;
      }).join('');
      
      return `<tr>${cells}</tr>`;
    }).join('');
  };

  const fetchData = useCallback(async () => {
    try {
      const [regularRes, specialRes, customRes, overridesRes, recipesRes, settingsRes] = await Promise.all([
        axios.get(`${API}/rotations/regular`),
        axios.get(`${API}/rotations/special`),
        axios.get(`${API}/rotations/custom`),
        axios.get(`${API}/calendar/overrides`),
        axios.get(`${API}/recipes`),
        axios.get(`${API}/rotation-settings`)
      ]);

      if (regularRes.data.length > 0) setRegularRotation(regularRes.data[0]);
      if (specialRes.data.length > 0) setSpecialRotation(specialRes.data[0]);
      setCustomRotations(customRes.data.filter(r => r.active));
      setOverrides(overridesRes.data);
      setRecipes(recipesRes.data.filter(r => r.active));
      
      // Set rotation start date if available
      if (settingsRes.data.rotation_start_date) {
        setRotationStartDate(parseISO(settingsRes.data.rotation_start_date));
        setTempStartDate(settingsRes.data.rotation_start_date);
      }
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Calculate which week number a date falls into based on rotation start date
  const getWeekNumber = (date, totalWeeks) => getRotationWeekNumber(date, rotationStartDate, totalWeeks);

  const saveRotationSettings = async () => {
    try {
      await axios.put(`${API}/rotation-settings`, {
        rotation_start_date: tempStartDate
      });
      setRotationStartDate(parseISO(tempStartDate));
      toast.success('Rotation start date saved! Calendar will now show correct weeks.');
      setShowSettingsDialog(false);
    } catch (error) {
      toast.error('Failed to save rotation settings');
    }
  };

  // Get custom rotation items for a specific date
  const getCustomRotationItems = (date) => {
    const dayName = format(date, 'EEEE');
    const items = {};
    
    customRotations.forEach(rotation => {
      if (rotation.days?.includes(dayName)) {
        const totalWeeks = rotation.rotation_length || 1;
        const weekNum = getWeekNumber(date, totalWeeks);
        const weekKey = `Week${weekNum}`;
        const recipe = getCustomDayRecipe(rotation.schedule, weekKey, dayName);
        if (recipe) {
          items[rotation.slot_name] = {
            recipe,
            rotationName: rotation.name,
            rotationId: rotation.id
          };
        }
      }
    });
    
    return items;
  };

  const getDayMenu = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const dayOfWeek = getDay(date); // 0 = Sunday, 6 = Saturday
    const dayName = format(date, 'EEEE');

    // Check for override first
    const override = overrides.find(o => o.date === dateStr);
    if (override) {
      if (override.override_type === 'off') {
        return { type: 'off', reason: override.reason || 'Day Off' };
      }
      return {
        type: 'override',
        sabzi: override.sabzi,
        daal: override.daal,
        salad: override.salad,
        extra: override.extra,
        main: override.main,
        roti: override.roti,
        raita: override.raita,
        side: override.side,
        reason: override.reason
      };
    }

    // Saturday is always OFF
    if (dayOfWeek === 6) {
      return { type: 'off', reason: 'Saturday' };
    }

    // Check if this day is in the special rotation
    if (specialRotation) {
      const selectedDays = specialRotation.days || ['Sunday'];
      if (selectedDays.includes(dayName)) {
        // Calculate which week in rotation
        const totalWeeks = specialRotation.rotation_length || 1;
        const weekNum = getWeekNumber(date, totalWeeks);
        const weekKey = `Week${weekNum}`;
        const daySchedule = getDaySchedule(specialRotation.schedule, weekKey, dayName);
        return {
          type: 'special',
          main: daySchedule.main,
          roti: daySchedule.roti,
          raita: daySchedule.raita,
          side: daySchedule.side
        };
      }
    }

    // Regular weekday
    if (!regularRotation) {
      return { type: 'regular', sabzi: null, daal: null, salad: null, extra: null };
    }
    
    const totalWeeks = regularRotation.rotation_length || 1;
    const weekNum = getWeekNumber(date, totalWeeks);
    const weekKey = `Week${weekNum}`;
    const daySchedule = getDaySchedule(regularRotation.schedule, weekKey, dayName);
    return {
      type: 'regular',
      sabzi: daySchedule.sabzi,
      daal: daySchedule.daal,
      salad: daySchedule.salad,
      extra: daySchedule.extra,
      weekNum: weekNum // Include week number for display
    };
  };

  const openOverrideDialog = (date) => {
    setSelectedDate(date);
    const menu = getDayMenu(date);
    
    setOverrideData({
      override_type: menu.type === 'special' ? 'special' : 'regular',
      sabzi: menu.sabzi || '',
      daal: menu.daal || '',
      salad: menu.salad || '',
      extra: menu.extra || '',
      main: menu.main || '',
      roti: menu.roti || '',
      raita: menu.raita || '',
      side: menu.side || '',
      reason: ''
    });
    setShowOverrideDialog(true);
  };

  const saveOverride = async () => {
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      await axios.post(`${API}/calendar/override`, {
        date: dateStr,
        ...overrideData
      });
      toast.success('Override saved');
      setShowOverrideDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to save override');
    }
  };

  const deleteOverride = async () => {
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      await axios.delete(`${API}/calendar/override/${dateStr}`);
      toast.success('Override removed');
      setShowOverrideDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to remove override');
    }
  };

  const goToProduction = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    navigate(`/production?date=${dateStr}`);
  };

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Pad start of month
  const startPadding = getDay(monthStart);
  const paddedDays = Array(startPadding).fill(null).concat(daysInMonth);

  const sabziRecipes = recipes.filter(r => r.category === 'Sabzi');
  const daalRecipes = recipes.filter(r => r.category === 'Daal');
  const saladRecipes = recipes.filter(r => r.category === 'Salad');
  const sweetRecipes = recipes.filter(r => r.category === 'Sweet');
  const mainRecipes = recipes.filter(r => r.category === 'SundayMain' || r.category === 'SpecialMain');
  const rotiRecipes = recipes.filter(r => r.category === 'SundayRoti' || r.category === 'SpecialRoti');
  const raitaRecipes = recipes.filter(r => r.category === 'SundayRaita' || r.category === 'SpecialRaita');
  const sideRecipes = recipes.filter(r => r.category === 'SundaySide' || r.category === 'SpecialSide');
  // Extra can be any recipe
  const extraRecipes = recipes;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="live-calendar-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-stone-800 tracking-tight" >
            Live Calendar
          </h1>
          <p className="text-stone-500 mt-1">
            View and override daily menus
            {rotationStartDate && (
              <span className="ml-2 text-[#124242]">
                • Week {getWeekNumber(new Date(), regularRotation?.rotation_length || 1)} of {regularRotation?.rotation_length || 1}
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <button
              onClick={() => setShowSettingsDialog(true)}
              className="btn-outline flex items-center gap-2"
              data-testid="rotation-settings-btn"
            >
              <Settings size={20} />
              <span className="hidden sm:inline">Week Sync</span>
            </button>
          )}
          <button
            onClick={handlePrint}
            className="btn-outline flex items-center gap-2"
            data-testid="print-calendar-btn"
          >
            <Printer size={20} />
            <span className="hidden sm:inline">Print Calendar</span>
          </button>
          <button
            onClick={() => handleSaveImage('portrait')}
            disabled={generatingImage}
            className="btn-outline flex items-center gap-2 disabled:opacity-60"
            data-testid="save-image-btn"
          >
            <ImageDown size={20} />
            <span className="hidden sm:inline">{generatingImage ? 'Generating...' : 'Save as Image'}</span>
          </button>
        </div>
      </div>

      {/* Week Sync Alert - show if no start date set */}
      {isAdmin && !rotationStartDate && (
        <div className="virsa-card p-4 bg-[#faa023]/10 border-[#faa023]/30">
          <div className="flex items-center gap-3">
            <Calendar size={24} className="text-[#faa023]" />
            <div className="flex-1">
              <p className="font-medium text-stone-800">Week Rotation Not Synced</p>
              <p className="text-sm text-stone-600">Set a start date to sync week rotations correctly.</p>
            </div>
            <button
              onClick={() => setShowSettingsDialog(true)}
              className="btn-primary text-sm"
            >
              Set Start Date
            </button>
          </div>
        </div>
      )}

      {/* Month Navigation */}
      <div className="virsa-card p-4">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
            className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
            data-testid="prev-month-btn"
          >
            <ChevronLeft size={24} />
          </button>
          <h2 className="text-2xl font-bold text-stone-800" >
            {format(currentMonth, 'MMMM yyyy')}
          </h2>
          <button
            onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
            className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
            data-testid="next-month-btn"
          >
            <ChevronRight size={24} />
          </button>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="virsa-card overflow-hidden" ref={printRef}>
        {/* Day headers */}
        <div className="grid grid-cols-7 bg-[#124242] text-white">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="p-3 text-center font-semibold text-sm">
              {day}
            </div>
          ))}
        </div>

        {/* Days */}
        <div className="grid grid-cols-7">
          {paddedDays.map((date, index) => {
            if (!date) {
              return <div key={`pad-${index}`} className="p-2 bg-stone-50 min-h-[100px]" />;
            }

            const menu = getDayMenu(date);
            const isToday = isSameDay(date, new Date());
            const isSunday = getDay(date) === 0;
            const isSaturday = getDay(date) === 6;
            const hasOverride = overrides.some(o => o.date === format(date, 'yyyy-MM-dd'));

            return (
              <div
                key={date.toISOString()}
                className={`p-2 border border-stone-100 min-h-[100px] ${
                  isToday ? 'bg-[#124242]/5 ring-2 ring-[#124242]' : ''
                } ${isSunday ? 'bg-[#faa023]/10' : ''} ${
                  menu.type === 'off' ? 'bg-stone-100' : ''
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-sm font-semibold ${isToday ? 'text-[#124242]' : 'text-stone-600'}`}>
                    {format(date, 'd')}
                  </span>
                  <div className="flex gap-1">
                    {hasOverride && (
                      <span className="w-2 h-2 rounded-full bg-[#faa023]" title="Has override" />
                    )}
                    {isSunday && <Sun size={12} className="text-[#faa023]" />}
                  </div>
                </div>

                {menu.type === 'off' ? (
                  <div className="text-center py-2">
                    <Pause size={16} className="mx-auto text-stone-400 mb-1" />
                    <span className="text-xs text-stone-400">{menu.reason}</span>
                  </div>
                ) : menu.type === 'special' ? (
                  <div className="space-y-0.5 text-xs">
                    {menu.main && <div className="truncate text-[#d6922b]">{menu.main}</div>}
                    {menu.roti && <div className="truncate text-stone-500">{menu.roti}</div>}
                    {menu.raita && <div className="truncate text-stone-500">{menu.raita}</div>}
                    {menu.side && <div className="truncate text-stone-500">{menu.side}</div>}
                    {/* Custom rotation items */}
                    {Object.entries(getCustomRotationItems(date)).map(([slot, item]) => (
                      <div key={slot} className="truncate text-purple-600" title={item.rotationName}>
                        {item.recipe}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-0.5 text-xs">
                    {menu.sabzi && <div className="truncate text-green-700">{menu.sabzi}</div>}
                    {menu.daal && <div className="truncate text-orange-700">{menu.daal}</div>}
                    {menu.salad && <div className="truncate text-cyan-700">{menu.salad}</div>}
                    {menu.extra && <div className="truncate text-pink-600">{menu.extra}</div>}
                    {/* Custom rotation items */}
                    {Object.entries(getCustomRotationItems(date)).map(([slot, item]) => (
                      <div key={slot} className="truncate text-purple-600" title={item.rotationName}>
                        {item.recipe}
                      </div>
                    ))}
                  </div>
                )}

                {menu.type !== 'off' && (
                  <div className="flex gap-1 mt-2">
                    <button
                      onClick={() => openOverrideDialog(date)}
                      className="flex-1 p-1 text-xs bg-stone-100 hover:bg-stone-200 rounded transition-colors"
                      title="Edit"
                    >
                      <Edit2 size={12} className="mx-auto" />
                    </button>
                    <button
                      onClick={() => goToProduction(date)}
                      className="flex-1 p-1 text-xs bg-[#124242] text-white hover:bg-[#0d3232] rounded transition-colors"
                      title="Generate Production"
                    >
                      <Factory size={12} className="mx-auto" />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="virsa-card p-4">
        <div className="flex flex-wrap gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-500" />
            <span>Sabzi</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-orange-500" />
            <span>Daal</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-cyan-500" />
            <span>Salad</span>
          </div>
          <div className="flex items-center gap-2">
            <Sun size={14} className="text-[#faa023]" />
            <span>Sunday Special</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[#faa023]" />
            <span>Has Override</span>
          </div>
        </div>
      </div>

      {/* Override Dialog */}
      <Dialog open={showOverrideDialog} onOpenChange={setShowOverrideDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl" >
              {selectedDate ? format(selectedDate, 'EEEE, MMMM d, yyyy') : 'Edit Day'}
            </DialogTitle>
            <DialogDescription>
              Override the menu for this specific day
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium text-stone-600 mb-2">Override Type</label>
              <Select
                value={overrideData.override_type}
                onValueChange={(v) => setOverrideData({ ...overrideData, override_type: v })}
              >
                <SelectTrigger className="h-12">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="regular">Regular Menu</SelectItem>
                  <SelectItem value="special">Special Menu</SelectItem>
                  <SelectItem value="off">Day Off</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {overrideData.override_type === 'regular' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-stone-600 mb-2">Sabzi</label>
                  <Select
                    value={overrideData.sabzi || '__none__'}
                    onValueChange={(v) => setOverrideData({ ...overrideData, sabzi: v === '__none__' ? '' : v })}
                  >
                    <SelectTrigger className="h-12">
                      <SelectValue placeholder="Select Sabzi" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">None</SelectItem>
                      {sabziRecipes.map(r => (
                        <SelectItem key={r.id} value={r.recipe_name}>{r.recipe_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-600 mb-2">Daal</label>
                  <Select
                    value={overrideData.daal || '__none__'}
                    onValueChange={(v) => setOverrideData({ ...overrideData, daal: v === '__none__' ? '' : v })}
                  >
                    <SelectTrigger className="h-12">
                      <SelectValue placeholder="Select Daal" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">None</SelectItem>
                      {daalRecipes.map(r => (
                        <SelectItem key={r.id} value={r.recipe_name}>{r.recipe_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-600 mb-2">Salad</label>
                  <Select
                    value={overrideData.salad || '__none__'}
                    onValueChange={(v) => setOverrideData({ ...overrideData, salad: v === '__none__' ? '' : v })}
                  >
                    <SelectTrigger className="h-12">
                      <SelectValue placeholder="Select Salad" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">None</SelectItem>
                      {saladRecipes.map(r => (
                        <SelectItem key={r.id} value={r.recipe_name}>{r.recipe_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-600 mb-2">Extra (Optional)</label>
                  <Select
                    value={overrideData.extra || '__none__'}
                    onValueChange={(v) => setOverrideData({ ...overrideData, extra: v === '__none__' ? '' : v })}
                  >
                    <SelectTrigger className="h-12">
                      <SelectValue placeholder="Select Extra Item" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">None</SelectItem>
                      {extraRecipes.map(r => (
                        <SelectItem key={r.id} value={r.recipe_name}>{r.recipe_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            {overrideData.override_type === 'special' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-stone-600 mb-2">Main Item</label>
                  <Select
                    value={overrideData.main || '__none__'}
                    onValueChange={(v) => setOverrideData({ ...overrideData, main: v === '__none__' ? '' : v })}
                  >
                    <SelectTrigger className="h-12">
                      <SelectValue placeholder="Select Main" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">None</SelectItem>
                      {mainRecipes.map(r => (
                        <SelectItem key={r.id} value={r.recipe_name}>{r.recipe_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-600 mb-2">Roti</label>
                  <Select
                    value={overrideData.roti || '__none__'}
                    onValueChange={(v) => setOverrideData({ ...overrideData, roti: v === '__none__' ? '' : v })}
                  >
                    <SelectTrigger className="h-12">
                      <SelectValue placeholder="Select Roti" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">None</SelectItem>
                      {rotiRecipes.map(r => (
                        <SelectItem key={r.id} value={r.recipe_name}>{r.recipe_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-600 mb-2">Raita</label>
                  <Select
                    value={overrideData.raita || '__none__'}
                    onValueChange={(v) => setOverrideData({ ...overrideData, raita: v === '__none__' ? '' : v })}
                  >
                    <SelectTrigger className="h-12">
                      <SelectValue placeholder="Select Raita" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">None</SelectItem>
                      {raitaRecipes.map(r => (
                        <SelectItem key={r.id} value={r.recipe_name}>{r.recipe_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-600 mb-2">Side</label>
                  <Select
                    value={overrideData.side || '__none__'}
                    onValueChange={(v) => setOverrideData({ ...overrideData, side: v === '__none__' ? '' : v })}
                  >
                    <SelectTrigger className="h-12">
                      <SelectValue placeholder="Select Side" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">None</SelectItem>
                      {sideRecipes.map(r => (
                        <SelectItem key={r.id} value={r.recipe_name}>{r.recipe_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            <div>
              <label className="block text-sm font-medium text-stone-600 mb-2">Reason (optional)</label>
              <input
                type="text"
                value={overrideData.reason}
                onChange={(e) => setOverrideData({ ...overrideData, reason: e.target.value })}
                className="virsa-input"
                placeholder="e.g., Special order, Holiday"
              />
            </div>
          </div>

          <DialogFooter className="flex gap-2">
            {overrides.some(o => o.date === (selectedDate ? format(selectedDate, 'yyyy-MM-dd') : '')) && (
              <button onClick={deleteOverride} className="btn-outline text-red-600 border-red-600 hover:bg-red-50">
                <X size={18} />
                Remove Override
              </button>
            )}
            <button onClick={() => setShowOverrideDialog(false)} className="btn-outline">
              Cancel
            </button>
            <button onClick={saveOverride} className="btn-primary">
              <Save size={18} />
              Save
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Week Sync Settings Dialog */}
      <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl flex items-center gap-2">
              <Calendar size={24} className="text-[#124242]" />
              Week Rotation Sync
            </DialogTitle>
            <DialogDescription>
              Set the date when Week 1 of your rotation starts. The calendar will automatically calculate which week to display based on this date.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium text-stone-600 mb-2">
                Week 1 Start Date
              </label>
              <input
                type="date"
                value={tempStartDate}
                onChange={(e) => setTempStartDate(e.target.value)}
                className="virsa-input"
                data-testid="rotation-start-date"
              />
              <p className="text-xs text-stone-500 mt-1">
                Select a Sunday to mark the beginning of Week 1 in your rotation.
              </p>
            </div>

            {tempStartDate && (
              <div className="p-3 bg-[#124242]/5 rounded-lg">
                <p className="text-sm font-medium text-stone-700">
                  Current Week: <span className="text-[#124242] font-bold">Week {getWeekNumber(new Date(), regularRotation?.rotation_length || 1)}</span>
                </p>
                <p className="text-xs text-stone-500 mt-1">
                  Based on {regularRotation?.rotation_length || 1}-week rotation cycle
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <button onClick={() => setShowSettingsDialog(false)} className="btn-outline">
              Cancel
            </button>
            <button onClick={saveRotationSettings} className="btn-primary" disabled={!tempStartDate}>
              <Save size={18} />
              Save Settings
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Calendar Image Preview Dialog */}
      <Dialog open={showImageDialog} onOpenChange={setShowImageDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="calendar-image-dialog">
          <DialogHeader>
            <DialogTitle>
              {imageLayout === 'chart' || imageLayout === 'chartv' ? 'Menu Rotation Chart' : `Calendar Image — ${format(currentMonth, 'MMMM yyyy')}`}
            </DialogTitle>
            <DialogDescription>
              Download this image to view or share it on your phone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            <button
              onClick={() => handleSaveImage('portrait')}
              disabled={generatingImage}
              className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${imageLayout === 'portrait' ? 'bg-[#124242] text-white border-[#124242]' : 'bg-white text-stone-600 border-stone-300 hover:border-stone-400'}`}
              data-testid="layout-portrait-btn"
            >
              Phone (Portrait)
            </button>
            <button
              onClick={() => handleSaveImage('landscape')}
              disabled={generatingImage}
              className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${imageLayout === 'landscape' ? 'bg-[#124242] text-white border-[#124242]' : 'bg-white text-stone-600 border-stone-300 hover:border-stone-400'}`}
              data-testid="layout-landscape-btn"
            >
              Print Grid (Landscape)
            </button>
            <button
              onClick={() => handleSaveImage('chart')}
              disabled={generatingImage}
              className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${imageLayout === 'chart' ? 'bg-[#124242] text-white border-[#124242]' : 'bg-white text-stone-600 border-stone-300 hover:border-stone-400'}`}
              data-testid="layout-chart-btn"
            >
              Rotation Chart
            </button>
            <button
              onClick={() => handleSaveImage('chartv')}
              disabled={generatingImage}
              className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${imageLayout === 'chartv' ? 'bg-[#124242] text-white border-[#124242]' : 'bg-white text-stone-600 border-stone-300 hover:border-stone-400'}`}
              data-testid="layout-chartv-btn"
            >
              Chart (Days Column)
            </button>
          </div>
          {generatingImage && (
            <p className="text-sm text-stone-500">Generating image...</p>
          )}
          {calendarImage && (
            <div className="space-y-4">
              <img
                src={calendarImage}
                alt={`Calendar ${format(currentMonth, 'MMMM yyyy')}`}
                className={`rounded-lg border border-stone-200 ${imageLayout === 'portrait' ? 'w-full max-w-md mx-auto' : 'w-full'}`}
                data-testid="calendar-image-preview"
              />
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                <p className="text-xs text-stone-500">
                  Tip: on your phone, you can also long-press the image to save or share it.
                </p>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={handleShareImage}
                    className="btn-outline flex items-center gap-2"
                    data-testid="share-image-btn"
                  >
                    <Share2 size={18} />
                    Share
                  </button>
                  <a
                    href={calendarImage}
                    download={getImageFileName()}
                    className="btn-primary flex items-center gap-2"
                    data-testid="download-image-btn"
                  >
                    <Download size={18} />
                    Download
                  </a>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
